import { type AsyncResult, createPendingAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { type CacheDatum, CacheDatumState, type LoadedCacheDatum } from "@/cache/CacheDatumState";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { cancelable } from "@/cache/utils";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { recordLayerFetchMs } from "@/otel/metrics";
import { MiB } from "@/utility/bytesize";
import {
  MeteomaticsApiUrl,
  type VectorLayerStyleRequest,
  type VectorTileRequestUnion,
  WORLD_BBOX,
} from "@mm/api.meteomatics.com";
import type { MeteomaticsApiError } from "@mm/api.meteomatics.com";
import { type Abort, isAbortable } from "@mm/api.meteomatics.com";
import type { Style } from "mapbox-gl";
import { FailureState, type RetryState, createRetryState } from "../SpatioTemporalTileCache/RetryState";

export interface VectorTileCacheStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
  tileCount: number;
  tilePending: number;
  tileEstimatedMemoryConsumption: number;
  styleCount: number;
  stylePending: number;
  styleEstimatedMemoryConsumption: number;
}

const DEFAULT_VECTOR_TILE_CACHE_MAX_SIZE = MiB(128);
const DEFAULT_VECTOR_TILE_CACHE_DELAY_EVICTION_SIZE = MiB(160);
const DEFAULT_VECTOR_TILE_STYLE_CACHE_MAX_SIZE = MiB(8);
const DEFAULT_VECTOR_TILE_STYLE_CACHE_DELAY_EVICTION_SIZE = MiB(12);

export class VectorTileCache implements Stats<VectorTileCacheStats> {
  protected styleCache: { [key: string]: CacheDatum<Style | undefined, RetryState> } = {};
  protected tilesCache: { [key: string]: CacheDatum<ArrayBuffer | undefined, RetryState> } = {};

  constructor(
    private readonly maxSize: number = DEFAULT_VECTOR_TILE_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_VECTOR_TILE_CACHE_DELAY_EVICTION_SIZE,
    private readonly maxStyleSize: number = DEFAULT_VECTOR_TILE_STYLE_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilStyleSize: number = DEFAULT_VECTOR_TILE_STYLE_CACHE_DELAY_EVICTION_SIZE,
  ) {}

  private markStyleEntryUsed(cacheLine: LoadedCacheDatum<Style | undefined>) {
    markCacheDatumUsed(cacheLine);
  }

  private markTileEntryUsed(cacheLine: LoadedCacheDatum<ArrayBuffer | undefined>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadStyleEntry(_: CacheDatum<Style | undefined, RetryState> | undefined, cacheId: string) {
    delete this.styleCache[cacheId];
  }

  private unloadTileEntry(_: CacheDatum<ArrayBuffer | undefined, RetryState> | undefined, cacheId: string) {
    delete this.tilesCache[cacheId];
  }

  private pruneStyleCache() {
    evictCacheEntriesBySize(
      this.styleCache,
      this.maxStyleSize,
      this.delayEvictionUntilStyleSize,
      this.unloadStyleEntry.bind(this),
    );
  }

  private pruneTileCache() {
    evictCacheEntriesBySize(
      this.tilesCache,
      this.maxSize,
      this.delayEvictionUntilSize,
      this.unloadTileEntry.bind(this),
    );
  }

  /**
   * Wraps logical cancelation with URL-based abort dispatch to the API worker.
   * The returned callback is idempotent and executes the provided cache cleanup exactly once.
   */
  private createCancelableWithAbort(cancel: () => void, requestUrls: string[], onCancel: () => void): () => void {
    let canceled = false;
    const dedupUrls = Array.from(new Set(requestUrls)).filter((url) => typeof url === "string" && url.length > 0);

    return () => {
      if (canceled) {
        return;
      }
      canceled = true;
      cancel();
      if (dedupUrls.length) {
        apiThreadPool.abortRequestByUrl(dedupUrls);
      }
      onCancel();
    };
  }

  retrieveVectorLayerStyle(request: VectorLayerStyleRequest): AsyncResult<Style | undefined> {
    const url = MeteomaticsApiUrl.forVectorLayerStyle(request);
    const cacheId = url.toString();

    let retry_: RetryState | null = null;

    if (Object.hasOwn(this.styleCache, cacheId)) {
      const v = this.styleCache[cacheId];
      switch (v.state) {
        case CacheDatumState.Pending:
          retry_ = v.payload;
          if (retry_.failureState !== FailureState.FailedTemporarily) {
            return createPendingAsyncResult(v.promise, v.cancel);
          }
          // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
          // mark it as such to prevent concurrent invocations from reentering the remaining function body
          retry_.failureState = undefined;
          break;
        case CacheDatumState.PermanentlyFailed:
          return createResolvedAsyncResult(undefined);
        case CacheDatumState.Loaded:
          this.markStyleEntryUsed(v);
          return createResolvedAsyncResult(v.payload);
        default: {
          const _exhaustive: never = v;
          return _exhaustive;
        }
      }
    }

    // TODO Vector tiles - get rid of BBOX in retry state
    // Because vector tile style isn't tied to model
    const retry = retry_ ?? createRetryState(WORLD_BBOX);
    const requestDatetime = performance.now();

    const cancelablePromise = cancelable<Style | undefined>(
      apiThreadPool
        .getVectorLayerStyle(request, retry)
        .then((style) => {
          const cacheLine = this.styleCache[cacheId];
          // Ignore stale completions from canceled/replaced requests.
          if (
            !cacheLine ||
            cacheLine.state !== CacheDatumState.Pending ||
            cacheLine.requestDatetime !== requestDatetime
          ) {
            return style;
          }

          this.styleCache[cacheId] = createLoadedCacheDatum(style, requestDatetime);
          this.markStyleEntryUsed(this.styleCache[cacheId] as LoadedCacheDatum<Style | undefined>);
          this.pruneStyleCache();
          return style;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          // TODO More robust error handling
          if (isAbortable(e)) {
            const cacheLine = this.styleCache[cacheId];
            if (
              cacheLine &&
              cacheLine.state === CacheDatumState.Pending &&
              cacheLine.requestDatetime === requestDatetime
            ) {
              delete this.styleCache[cacheId];
            }
          }
          return Promise.reject(e);
        }),
    );
    // Style responses do not have a retry fallback; drop stale pending entry immediately on cancel.
    const cancel = this.createCancelableWithAbort(cancelablePromise.cancel, [url.toString()], () => {
      delete this.styleCache[cacheId];
    });
    this.styleCache[cacheId] = {
      requestDatetime,
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  retrieveVectorTile(
    // As it's possible to have more than one source inside vector tile
    // Make sure we keep each source > tiles separate in cache with unique ID
    { source, ...request }: VectorTileRequestUnion & { source: string },
  ): AsyncResult<ArrayBuffer | undefined> {
    const url: MeteomaticsApiUrl = MeteomaticsApiUrl.forVectorTile(request);
    const cacheId = `${url.toString()}_${source}`;
    let retry_: RetryState | null = null;

    if (Object.hasOwn(this.tilesCache, cacheId)) {
      const v = this.tilesCache[cacheId];
      switch (v.state) {
        case CacheDatumState.Pending:
          retry_ = v.payload;
          if (retry_.failureState !== FailureState.FailedTemporarily) {
            return createPendingAsyncResult(v.promise, v.cancel);
          }
          // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
          // mark it as such to prevent concurrent invocations from reentering the remaining function body
          retry_.failureState = undefined;
          break;
        case CacheDatumState.PermanentlyFailed:
          return createResolvedAsyncResult(new ArrayBuffer(0));
        case CacheDatumState.Loaded:
          this.markTileEntryUsed(v);
          return createResolvedAsyncResult(v.payload);
        default: {
          const _exhaustive: never = v;
          return _exhaustive;
        }
      }
    }

    const modelBoundingBox = request.boundingBoxLimit || WORLD_BBOX;
    const retry = retry_ ?? createRetryState(modelBoundingBox);
    const requestDatetime = performance.now();

    const fetchStarted = performance.now();
    const cancelablePromise = cancelable<ArrayBuffer | undefined>(
      apiThreadPool
        .getVectorTile(request, retry)
        .then((arrayBuffer) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "vector_tile",
          });
          const cacheLine = this.tilesCache[cacheId];
          // Ignore stale completions from canceled/replaced requests.
          if (
            !cacheLine ||
            cacheLine.state !== CacheDatumState.Pending ||
            cacheLine.requestDatetime !== requestDatetime
          ) {
            return arrayBuffer;
          }

          this.tilesCache[cacheId] = createLoadedCacheDatum(arrayBuffer, requestDatetime);
          this.markTileEntryUsed(this.tilesCache[cacheId] as LoadedCacheDatum<ArrayBuffer | undefined>);
          this.pruneTileCache();

          return arrayBuffer;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          // TODO More robust error handling
          if (isAbortable(e)) {
            const cacheLine = this.tilesCache[cacheId];
            if (
              cacheLine &&
              cacheLine.state === CacheDatumState.Pending &&
              cacheLine.requestDatetime === requestDatetime
            ) {
              delete this.tilesCache[cacheId];
            }
          }
          console.error(e);
          // For now, resolve with empty array buffer to prevent
          // metx chocking with rejected errors
          return Promise.resolve(new ArrayBuffer(0));
          // return Promise.reject(e);
        }),
    );
    // Keep tile cache coherent with UI cancelation by clearing the pending slot right away.
    const cancel = this.createCancelableWithAbort(cancelablePromise.cancel, [url.toString()], () => {
      delete this.tilesCache[cacheId];
    });

    this.tilesCache[cacheId] = {
      requestDatetime,
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };

    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  stats(): VectorTileCacheStats {
    let tileCount = 0;
    let tilePending = 0;
    let styleCount = 0;
    let stylePending = 0;
    const tileEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.tilesCache);
    const styleEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.styleCache);

    for (const line in this.tilesCache) {
      if (Object.hasOwn(this.tilesCache, line)) {
        tileCount++;

        if (this.tilesCache[line].state === CacheDatumState.Pending) {
          tilePending++;
        }
      }
    }

    for (const line in this.styleCache) {
      if (Object.hasOwn(this.styleCache, line)) {
        styleCount++;

        if (this.styleCache[line].state === CacheDatumState.Pending) {
          stylePending++;
        }
      }
    }

    return {
      count: tileCount + styleCount,
      pending: tilePending + stylePending,
      estimatedMemoryConsumption: tileEstimatedMemoryConsumption + styleEstimatedMemoryConsumption,
      tileCount,
      tilePending,
      tileEstimatedMemoryConsumption,
      styleCount,
      stylePending,
      styleEstimatedMemoryConsumption,
    };
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  label(): string {
    return "Mapbox vector tiles cache";
  }

  clear() {
    this.tilesCache = {};
    this.styleCache = {};
  }

  descJsx(): JSX.Element {
    return (
      <>
        Special-purpose cache for vector tile requests. Replacement policy is approximate memory-based LRU with separate
        tile and style pools.
      </>
    );
  }
}
